import { Node, NodeProps } from "@xyflow/react";
import { Suspense } from "react";
import { useAtomValue } from "jotai";
import classNames from "classnames";
import { SkeletonBlock, SkeletonText } from "@zapier/design-system";
import {
  isMultiSelectedAtomFamily,
  edgesAtom,
  isOnlyNodeSelectedAtomFamily,
  DefaultNodeData,
  shouldShowStepIdsAtom,
  shouldShowAddStepButtonAtom,
  isViewOnlyModeAtom,
  aiSuggestedEditsAtomFamily,
  aiSuggestedEditsLoadingAtomFamily,
  areHandlesVisibleAtomFamily,
  NodeType,
  isValidUUID,
  togglesAtomResettable,
  isNodeLockedAtomFamily,
} from "@zapier/canvas-state";

import { AddStepButton } from "../../../../components/AddStepButton";
import { SmartFlyout } from "../../../../components/SmartFlyout/SmartFlyout";
import { StepId } from "../../../StepId";
import hasParent from "../../../../utils/hasParent";
import { determineAssetType } from "../../../../utils/determineAssetType";
import { getDefaultColorForAssetType } from "../../../../utils/getDefaultColorForAssetType";
import { isAssetNode } from "../../../../utils/isAssetNode";
import { getDefaultBackgroundColorForAssetType } from "../../../../utils/getDefaultBackgroundColorForAssetType";
import { NodeHoverWrapper } from "../../../NodeHoverWrapper";
import { FloatingInlineToolbar } from "../../../FloatingInlineToolbar";
import { Handles } from "../../../Handles";

import { DefaultNodeContent } from "./DefaultNodeContent";
import styles from "./DefaultNode.module.css";

function DefaultNodeInner(props: NodeProps<Node<DefaultNodeData>>) {
  const { id, data, type } = props;
  const assetType = determineAssetType(data.services, type as NodeType);
  const backgroundColor = getDefaultBackgroundColorForAssetType(
    assetType,
    data.color, // we need actual color without fallback from getDefaultColorForAssetType
  );
  const { color = getDefaultColorForAssetType(assetType) } = data;

  const isAssetNodeType = isAssetNode(data);
  const edges = useAtomValue(edgesAtom);
  const shouldShowStepIds = useAtomValue(shouldShowStepIdsAtom);
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const isMultiSelected = useAtomValue(isMultiSelectedAtomFamily(id));
  const shouldShowAddStepButton = useAtomValue(shouldShowAddStepButtonAtom);
  const aiSuggestedEdits = useAtomValue(aiSuggestedEditsAtomFamily(id));
  const aiSuggestedEditsLoading = useAtomValue(
    aiSuggestedEditsLoadingAtomFamily(id),
  );
  const isSelected = useAtomValue(isOnlyNodeSelectedAtomFamily(id));
  const areHandlesVisible = useAtomValue(areHandlesVisibleAtomFamily(id));
  const toggles = useAtomValue(togglesAtomResettable);

  const isNodeLocked = useAtomValue(isNodeLockedAtomFamily(id));
  const isToolbarVisible =
    !isNodeLocked && !isViewOnlyMode && isSelected && isValidUUID(id);

  return (
    <>
      {shouldShowStepIds && <StepId />}
      {!hasParent(edges, id) && shouldShowAddStepButton && (
        <AddStepButton handleType="target" />
      )}
      {shouldShowAddStepButton && <AddStepButton handleType="source" />}
      <NodeHoverWrapper>
        {isToolbarVisible && (
          <FloatingInlineToolbar
            color={
              !toggles.processAssetTypeNodes && isAssetNodeType
                ? undefined // For old node design, we don't allow users to change the color for asset nodes
                : color
            }
            selectedApps={data.services}
            actors={data.actors}
            hasNotes={Boolean(data.notesJson)}
            link={data.link}
            nodeType={type as NodeType}
            assetIds={{
              tableId: data.tableId,
              interfaceProjectId: data.projectId,
              interfacePageId: data.pageId,
              chatbotId: data.chatbotId,
            }}
          />
        )}
        <DefaultNodeContent
          {...props}
          data={{ ...data, color }}
          className={classNames({
            [styles.wrapperMultiSelected]: isMultiSelected,
            [styles.wrapperSelected]: !toggles.processAssetTypeNodes
              ? isViewOnlyMode
                ? isSelected
                : isToolbarVisible
              : false,
            [styles.wrapperSingleSelected]: toggles.processAssetTypeNodes
              ? isSelected
              : false,
          })}
          isViewOnlyMode={isViewOnlyMode || isNodeLocked}
          backgroundColor={backgroundColor}
        />

        <Handles nodeType="default" isVisible={areHandlesVisible} />
        {((aiSuggestedEdits?.additions &&
          aiSuggestedEdits.additions.length > 0) ||
          aiSuggestedEditsLoading) && <SmartFlyout nodeId={id} />}
      </NodeHoverWrapper>
    </>
  );
}

export function DefaultNode(props: NodeProps<Node<DefaultNodeData>>) {
  return (
    <Suspense fallback={<LoadingNode />}>
      <DefaultNodeInner {...props} />
    </Suspense>
  );
}

/* IMPORTANT! We need Handles in the loading state or else the edges won't get rendered even though they exist */
function LoadingNode() {
  return (
    <div className={styles.wrapper}>
      <Handles nodeType="default" />
      <SkeletonText lines={1} size="heading" textAlign="left" width="100%" />
      <SkeletonBlock borderRadius="small" height="30px" width="30px" />
    </div>
  );
}
